import { Injector, OnInit, OnDestroy, Input, Directive } from '@angular/core';
import { BaseComponent, environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { BaseModelArray } from '@eview/core/base/base-model';
import {
  FormAttribute,
  FormAttributeConfig,
  FormAttributeConfigAction,
  FormAttributeInput,
  FormAttributeType,
  getFilterByParentRule
} from '@eview/core/domain/form/form-attribute';
import { FormValue } from '@eview/core/domain/form/form-value';
import { FormHelpers } from '@eview/core/domain/form/form.helpers';
import { FormService } from '@eview/core/domain/form/form.service';
import {
  LEGACY_VULNERABILITY_ATTR_KEYS,
  isRepeatableRowsAttribute,
  parseVulnerabilityRows,
  resolveRowsCatalog,
  validateVulnerabilityRows
} from '@eview/core/domain/form/vulnerability-rows';
import { Media } from '@eview/core/domain/media/media';
import { MediaService } from '@eview/core/domain/media/media.service';
import { Post, PostPriority, PostStatus } from '@eview/core/domain/post/post';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { PostService } from '@eview/core/domain/post/post.service';
import { TagHelpers } from '@eview/core/domain/tags/tag.helpers';
import { User } from '@eview/core/models/user';
import {
  EMapActions,
  UserClickedMap
} from '@eview/core/store/actions/map.actions';
import {
  SubmitPostFailure,
  SubmitPostSuccess
} from '@eview/core/store/actions/posts.actions';
import { selectFormAttributes } from '@eview/core/store/selectors/form-attributes.selector';
import { selectUsers } from '@eview/core/store/selectors/users.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { UsersHelpers } from '@eview/core/users/users.helpers';
import { Actions, ofType } from '@ngrx/effects';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { Tag, Tags } from '@eview/core/domain/tags/tag';
import { PostFilters, RiskFilters } from '@eview/core/domain/post/post';
import { globals } from '@eview/features/ui/post-editor/form-attribute-base.component';
import { isEmpty } from 'lodash';
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { Tenant } from '@eview/core/models/tenant';
import { CatalogFacade } from '@eview/core/store/facades/catalog.facade';

@Directive()
export class PostEditorBaseComponent extends BaseComponent
  implements OnInit, OnDestroy {
  @Input() public postId: any;
  /** When true (create-report modal), loads fields via API without updating global NgRx. */
  @Input() isolateFormAttributes = false;
  formId: number = environment.form.id;
  tags$: Observable<Tags>;
  /** In-memory tag catalog from NgRx (loaded after login). */
  private tagCatalog: Tag[] = [];
  isSmsEditing: boolean = false;
  formAttrData: any;
  RiskFilters = RiskFilters;
  constructor(public injector: Injector, protected id: number) {
    super();
    this.store = injector.get(Store) as Store<AppState>;
    this.actions$ = injector.get(Actions) as Actions;
    this.formService = injector.get(FormService);
    this.mediaService = injector.get(MediaService);
    this.postService = injector.get(PostService);

    this.tags$ = this.store.select(selectTags)

    this.subs = new Subscription();
  }

  ngOnInit() {
    // Tag options for form fields — load catalog once when needed (not at app boot).
    this.injector.get(CatalogFacade).ensureTags();

    // When postId is passed on edit
    if (this.postId) {
      this.id = this.postId;
    }
    this.user$ = this.store.select(selectUsers).pipe(
      map(users => {
        const userId = this.post?.user?.id;
        return userId != null
          ? UsersHelpers.FindUserById(users, userId)
          : null;
      })
    );

    if (!this.isolateFormAttributes) {
      this.subs.add(
        combineLatest(
          this.store.select(selectFormAttributes).pipe(
            map(fas => {
              if (!fas?.results) {
                return null;
              }
              const updatedResults = fas.results
                .filter(
                  fa =>
                    !fa.response_private &&
                    !LEGACY_VULNERABILITY_ATTR_KEYS.has(fa.key)
                )
                .map(fa => ({
                ...fa,
                value: null
              })) as FormAttribute[];
              const sortedFas = FormHelpers.SortFormAttributes({
                ...fas,
                results: updatedResults
              });
              return sortedFas as FormAttributesEx;
            }),
            filter((fas): fas is FormAttributesEx => fas != null)
          ),
          this.store.select(selectTenant),
          this.id ? this.postService.read(this.id) : of(null)
        ).subscribe(([fas, tenant, post]) => {
          this.applyEditorFormAttributes(fas, tenant, post);
        })
      );
    }

    this.subs.add(
      this.tags$.subscribe(tags => {
        this.tagCatalog = tags?.results ?? [];
      })
    );

    this.subs.add(
      combineLatest(
        this.actions$
          .pipe(ofType<UserClickedMap>(EMapActions.UserClickedMap)),
        this.store.select(selectTenant)
      ).subscribe(([action, tenant]) => {
        if (
          !action ||
          !action.payload ||
          !action.payload.actual ||
          !action.payload.actual.properties
        ) {
          if (action.payload.actual !== null) return;
        }
        this.post = {
          ...this.post,
          ...PostHelpers.ParseMgmtLevels(action.payload.actual, tenant)
        };
      })
    );
  }

  /**
   * Loads form fields for the report editor without writing to NgRx (used by create-report modal).
   */
  loadEditorFormAttributes(formId: number, onReady?: () => void): void {
    this.subs.add(
      combineLatest(
        this.formService.listAttributes(formId),
        this.store.select(selectTenant),
        this.id ? this.postService.read(this.id) : of(null)
      ).subscribe(([fas, tenant, post]) => {
        if (!fas?.results) {
          this.formAttributesEx = null;
          return;
        }
        const updatedResults = fas.results
          .filter(
            fa =>
              !fa.response_private &&
              !LEGACY_VULNERABILITY_ATTR_KEYS.has(fa.key)
          )
          .map(fa => ({
          ...fa,
          value: null
        })) as FormAttribute[];
        const sortedFas = FormHelpers.SortFormAttributes({
          ...fas,
          results: updatedResults
        }) as FormAttributesEx;
        this.applyEditorFormAttributes(sortedFas, tenant, post);
        onReady?.();
      })
    );
  }

  private applyEditorFormAttributes(
    fas: FormAttributesEx,
    tenant: Tenant,
    post: Post | null
  ): void {
    this.isEditing = !!post;
    if (this.isEditing) {
      this.post = post;
    } else {
      this.subs.add(
        this.store.select(selectMap).subscribe(map => {
          this.post = {
            ...this.post,
            ...PostHelpers.ParseMgmtLevels(map.actual, tenant)
          };
        })
      );
    }
    this.formAttributesEx = this.bindPostValues(post, fas);
  }

  updateFormAttributesEx() {
    const languageCode = localStorage.getItem('languageCode');
      this.formAttributesEx.results.forEach((element, index) => {
        if (languageCode === 'en') {
          this.formAttributesEx.results[index]['title'] = element.label;
        } else {
          this.formAttributesEx.results[index]['title'] = element['label_' + languageCode];
        }
      });
  }

  ngOnDestroy() {
    this.tagCatalog = [];
    this.subs.unsubscribe();
  }

  store: Store<AppState>;
  actions$: Actions;
  formService: FormService;
  mediaService: MediaService;
  postService: PostService;

  formAttributesEx: FormAttributesEx;

  isEditing: boolean = false;
  post$: Observable<Post> = of(null);
  post: Post;

  PostHelpers = PostHelpers;
  UsersHelpers = UsersHelpers;
  AuthHelpers = AuthHelpers;
  AllowedPriviledge = AllowedPriviledge;
  PostStatus = PostStatus;
  PostPriority = PostPriority;

  format = environment.format;

  user$: Observable<User>;

  private subs: Subscription;

  get managementLevels() {
    return PostHelpers.FormatMgmtLevels(this.post);
  }

  /**
   * True when a filter_by_parent field's parent changed in this submission, so the
   * previously saved child no longer belongs to it and must not be restored.
   */
  private hasChangedParentDriver(
    fa: FormAttributeEx,
    submittedValues: { [key: string]: any }
  ): boolean {
    const parentKey = getFilterByParentRule(fa.config)?.target?.key;
    if (!parentKey) {
      return false;
    }
    const pick = (v: unknown) => (Array.isArray(v) ? v[0] : v);
    const submitted = pick(submittedValues?.[parentKey]);
    const original = pick(this.post?.values?.[parentKey]);
    return String(submitted ?? '') !== String(original ?? '');
  }

  /** Same visibility as conditional UI rules in form-attribute (for validation). */
  private isFieldVisibleForValidation(fa: FormAttributeEx): boolean {
    const filterRule = getFilterByParentRule(fa.config);
    if (filterRule?.target?.key) {
      const parentId = this.getAttributeScalarValue(filterRule.target.key);
      if (this.isEmptyFieldValue(parentId)) {
        return false;
      }
      // Match UI: hide when the selected parent has no children in this taxonomy.
      if (this.tagCatalog?.length) {
        const taxonomy = TagHelpers.tagTaxonomyKeyFromFormAttribute({
          input: fa.input,
          label: fa.label,
          type: fa.type,
          key: fa.key,
          config: fa.config
        });
        const index = TagHelpers.buildTagIndex({
          results: this.tagCatalog,
          count: this.tagCatalog.length,
          total_count: this.tagCatalog.length
        } as any);
        const kids = TagHelpers.childrenForParent(
          index,
          Number(parentId),
          taxonomy
        );
        if (!kids?.length) {
          return false;
        }
      }
    }

    const showRules = (fa.config || []).filter(
      c =>
        c?.action === FormAttributeConfigAction.Show &&
        c?.target?.key != null &&
        c?.target?.key !== ''
    );
    if (!showRules.length) {
      return true;
    }
    return showRules.some(rule => this.configRuleMatches(rule));
  }

  private getAttributeScalarValue(key: string): unknown {
    const source = this.formAttributesEx?.results?.find(r => r.key === key);
    if (source?.value == null || source?.value === '') {
      return null;
    }
    return Array.isArray(source.value) ? source.value[0] : source.value;
  }

  private isEmptyFieldValue(value: unknown): boolean {
    return (
      value == null ||
      value === '' ||
      value === -1 ||
      value === '-1'
    );
  }

  private configRuleMatches(rule: FormAttributeConfig): boolean {
    if (rule?.target?.key == null) {
      return false;
    }
    const source = this.formAttributesEx?.results?.find(
      r => r.key === rule.target.key
    );
    if (!source) {
      return false;
    }
    const values = Array.isArray(source.value)
      ? source.value
      : source.value == null || source.value === ''
        ? []
        : [source.value];
    if (!values.length) {
      return false;
    }
    const expected = rule.target.value;
    return values.some(
      v =>
        !this.isEmptyFieldValue(v) &&
        (v == expected || String(v) === String(expected))
    );
  }

  private isRequiredValueMissing(fa: FormAttributeEx): boolean {
    if (fa.value == null || fa.value === '') {
      return true;
    }
    if (isRepeatableRowsAttribute(fa)) {
      const rows = parseVulnerabilityRows(fa.value);
      return (
        !rows.length ||
        !!validateVulnerabilityRows(
          rows,
          true,
          resolveRowsCatalog(fa.config)
        )
      );
    }
    if (
      fa.type === FormAttributeType.Varchar &&
      fa.input === FormAttributeInput.Select
    ) {
      const v = Array.isArray(fa.value) ? fa.value[0] : fa.value;
      return this.isEmptyFieldValue(v);
    }
    if (fa.type === FormAttributeType.Tags) {
      const v = Array.isArray(fa.value) ? fa.value[0] : fa.value;
      return this.isEmptyFieldValue(v);
    }
    if (fa.type === FormAttributeType.Point) {
      const point = Array.isArray(fa.value) ? fa.value[0] : fa.value;
      return !(point && point.lat != null && point.lon != null);
    }
    if (fa.type === FormAttributeType.Media) {
      if (fa.value?.name) {
        return false;
      }
      if (Array.isArray(fa.value) && fa.value.length > 0) {
        return false;
      }
      if (fa.value?.id) {
        return false;
      }
      return true;
    }
    if (fa.type === FormAttributeType.DateTime) {
      if (Array.isArray(fa.value)) {
        const raw = fa.value[0];
        return raw == null || raw === '';
      }
      return fa.value == null || fa.value === '';
    }
    if (
      fa.type === FormAttributeType.Title ||
      fa.type === FormAttributeType.Content || // alias of Description
      fa.type === FormAttributeType.Text
    ) {
      const v = fa.value;
      if (typeof v === 'string') {
        return v.trim() === '';
      }
      const first = Array.isArray(v) ? v[0] : v;
      return first == null || String(first).trim() === '';
    }
    if (Array.isArray(fa.value)) {
      return this.isEmptyFieldValue(fa.value[0]);
    }
    return false;
  }

  isFormInvalid(): boolean {
    if (!this.formAttributesEx?.results?.length) {
      return true;
    }
    const requiredFieldsNotFilled = this.formAttributesEx.results.filter(fa => {
      if (!fa.required) return false;
      if (!this.isFieldVisibleForValidation(fa)) return false;
      return this.isRequiredValueMissing(fa);
    });
    const numberFieldsInvalid = this.formAttributesEx.results.some(fa => {
      if (!fa.required || fa.input !== FormAttributeInput.Number) {
        return false;
      }
      const rule = fa.config?.[0];
      if (!rule?.target?.key) {
        return false;
      }
      return this.formAttributesEx.results.some(a => {
        if (
          a.key === rule.target.key &&
          a.value &&
          a.value[0] == rule.target.value
        ) {
          return !(fa.value > 0);
        }
        return false;
      });
    });

    const vulnerabilityRowsInvalid = this.formAttributesEx.results.some(fa => {
      if (!fa.required || !isRepeatableRowsAttribute(fa)) {
        return false;
      }
      if (!this.isFieldVisibleForValidation(fa)) {
        return false;
      }
      const catalog = resolveRowsCatalog(fa.config);
      return !!validateVulnerabilityRows(
        parseVulnerabilityRows(fa.value),
        fa.required,
        catalog
      );
    });

    const dateTimeClientInvalid = this.formAttributesEx.results.some(fa => {
      if (fa.type !== FormAttributeType.DateTime) {
        return false;
      }
      if (!this.isFieldVisibleForValidation(fa)) {
        return false;
      }
      if (!fa.clientInvalid) {
        return false;
      }
      // Clear stale flag when the stored wall-clock value is actually within range
      // (UI may already look valid while clientInvalid was left true).
      const raw = Array.isArray(fa.value) ? fa.value[0] : fa.value;
      const selectedStr = this.wallClockFromStoredDateTime(raw);
      if (selectedStr) {
        const nowStr = this.localNowDateTimeString();
        if (selectedStr <= nowStr && selectedStr >= '2018-01-01T00:00') {
          fa.clientInvalid = false;
          return false;
        }
      }
      return true;
    });

    return (
      requiredFieldsNotFilled.length > 0 ||
      numberFieldsInvalid ||
      vulnerabilityRowsInvalid ||
      dateTimeClientInvalid
    );
  }

  /** Wall-clock `YYYY-MM-DDTHH:mm` from stored ISO/MySQL (digits = local time). */
  private wallClockFromStoredDateTime(raw: unknown): string | null {
    if (raw == null || raw === '') {
      return null;
    }
    const match = String(raw).match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/
    );
    if (!match) {
      return null;
    }
    return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
  }

  private localNowDateTimeString(): string {
    const now = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(
      now.getDate()
    )}T${p(now.getHours())}:${p(now.getMinutes())}`;
  }

  /**
   * Post API always expects title/content columns. Map attribute values to a plain string.
   */
  private isPlaceholderFieldValue(value: unknown): boolean {
    return (
      value == null ||
      value === '' ||
      value === -1 ||
      value === '-1'
    );
  }

  private normalizeAttributeValueToString(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    if (Array.isArray(value)) {
      const v = value[0];
      if (this.isPlaceholderFieldValue(v) || typeof v === 'object') {
        return null;
      }
      const s = String(v).trim();
      return s === '' ? null : s;
    }
    if (typeof value === 'object') {
      return null;
    }
    if (this.isPlaceholderFieldValue(value)) {
      return null;
    }
    const s = String(value).trim();
    return s === '' ? null : s;
  }

  /**
   * When the form has no title attribute, use a short text field so the API
   * still receives posts.title. If the form has a title field left empty, respect that.
   */
  private pickFallbackTitle(formAttributesEx: FormAttributesEx): {
    text: string;
    sourceKey?: string;
  } {
    const results = formAttributesEx?.results || [];
    const hasTitle = results.some(fa => fa.type === FormAttributeType.Title);
    if (hasTitle) {
      return { text: '' };
    }
    for (const fa of results) {
      if (
        fa.type === FormAttributeType.Title ||
        fa.type === FormAttributeType.Content
      ) {
        continue;
      }
      if (
        fa.type === FormAttributeType.Varchar &&
        (fa.input === FormAttributeInput.Text ||
          fa.input === FormAttributeInput.Video ||
          fa.input == null)
      ) {
        const s = this.normalizeAttributeValueToString(fa.value);
        if (s) {
          return {
            text: s.length > 150 ? s.slice(0, 150) : s,
            sourceKey: fa.key
          };
        }
      }
    }
    return { text: '' };
  }

  /**
   * Only used when the form has NO description attribute. Never copy other
   * fields (e.g. "Local de votación") into posts.content when description exists but is empty.
   */
  private pickFallbackContent(
    formAttributesEx: FormAttributesEx,
    excludeVarcharKey?: string
  ): string {
    const results = formAttributesEx?.results || [];
    if (results.some(fa => fa.type === FormAttributeType.Description)) {
      return '';
    }
    for (const fa of results) {
      if (fa.type === FormAttributeType.Text) {
        // Repeatable rows (vulnerabilidad_filas) are type=text but must never
        // become posts.content / Descripción.
        if (isRepeatableRowsAttribute(fa)) {
          continue;
        }
        const s = this.normalizeAttributeValueToString(fa.value);
        if (s) {
          return s;
        }
      }
    }
    for (const fa of results) {
      if (fa.type !== FormAttributeType.Varchar) {
        continue;
      }
      if (fa.input === FormAttributeInput.Select) {
        continue;
      }
      if (excludeVarcharKey != null && fa.key === excludeVarcharKey) {
        continue;
      }
      if (isRepeatableRowsAttribute(fa)) {
        continue;
      }
      const s = this.normalizeAttributeValueToString(fa.value);
      if (s) {
        return s;
      }
    }
    return '';
  }

  private getFormValue(formAttributesEx: FormAttributesEx): FormValue {
    const getFormAttributeByType = (
      formAttributes: FormAttributesEx,
      type: FormAttributeType
    ) =>
      formAttributes && formAttributes.results
        ? formAttributes.results.find(fa => fa.type === type)
        : null;
    const getValuesFromFormAttributes = (formAttributes: FormAttributesEx) =>
      (formAttributes?.results || [])
        .filter(
          fa =>
            fa.type !== FormAttributeType.Title &&
            fa.type !== FormAttributeType.Content &&
            fa.value !== null &&
            fa.value !== undefined
        )
        .map(fa => ({ [fa.key]: fa.value }));

    const getFormAttributeByLabel = (
          formAttributes: FormAttributesEx,
          value: string,
        
        ) =>
          formAttributes && formAttributes.results
            ? formAttributes.results.find(fa => fa.label === value)
            : null;
    const values = getValuesFromFormAttributes(formAttributesEx);
    const titleAttr = getFormAttributeByType(
      formAttributesEx,
      FormAttributeType.Title
    );
    const contentAttr = getFormAttributeByType(
      formAttributesEx,
      FormAttributeType.Content
    );

    let titleStr = this.normalizeAttributeValueToString(titleAttr?.value);
    let titleFallbackKey: string | undefined;
    if (!titleStr) {
      const fb = this.pickFallbackTitle(formAttributesEx);
      titleStr = fb.text;
      titleFallbackKey = fb.sourceKey;
    } else if (titleStr.length > 150) {
      titleStr = titleStr.slice(0, 150);
    }

    let contentStr = this.normalizeAttributeValueToString(contentAttr?.value);
    if (!contentStr) {
      contentStr = this.pickFallbackContent(
        formAttributesEx,
        titleFallbackKey
      );
    }

    const buscarPorIdTags = (idBuscado: number | string) =>
      this.tagCatalog.find(tag => tag.id == idBuscado);

    const proceso = getFormAttributeByLabel(
      formAttributesEx,
      'Proceso Electoral'
    );
    const institucion = getFormAttributeByLabel(
      formAttributesEx,
      'Institución'
    );
    const procesoId = proceso?.value?.[0];
    const institucionId = institucion?.value?.[0];
    let procesoName: string | null = null;
    let institucionName: string | null = null;
    if (procesoId != null && institucionId != null && this.tagCatalog.length > 0) {
      const tagFound = buscarPorIdTags(procesoId);
      const tagFoundInst = buscarPorIdTags(institucionId);
      procesoName = tagFound?.slug ?? null;
      institucionName = tagFoundInst?.slug ?? null;
    }

    return {
      title: titleStr ?? '',
      content: contentStr ?? '',
      proceso: procesoName,
      institucion: institucionName,
      values:
        values && values.length > 0
          ? values.reduce((acc, value) => ({ ...acc, ...value }))
          : {},
      form: { id: this.formId }
    };
  }

  private bindPostValues(
    post: Post,
    formAttributes: FormAttributesEx
  ): FormAttributesEx {
    if (!post) {
      return formAttributes;
    }

    const fasData = { ...formAttributes };
    if (!(fasData && fasData.results)) {
      return null;
    }
    const titleFa = fasData.results.find(
      fa => fa.type === FormAttributeType.Title
    );
    if (titleFa) {
      titleFa.value = post.title;
    }
    const contentFa = fasData.results.find(
      fa => fa.type === FormAttributeType.Content
    );
    if (contentFa) {
      contentFa.value = post.content;
    }
    fasData.results = fasData.results.map(fa => {
      if (
        fa.type !== FormAttributeType.Title &&
        fa.type !== FormAttributeType.Content
      ) {
        const raw = post.values?.[fa.key];
        if (raw == null) {
          fa.value = null;
        } else if (fa.type === FormAttributeType.Media) {
          fa.value = raw;
        } else if (Array.isArray(raw)) {
          fa.value = raw;
        } else {
          fa.value = [raw];
        }
      }
      return fa;
    });
    return fasData;
  }

  private handleMedias(
    formAttributesEx: FormAttributesEx
  ): Observable<MediaEx>[] | Observable<null> {
    const value = this.getFormValue(formAttributesEx);
    const mediaUploads = [];
    
    Object.keys(value.values).forEach(key => {
      const val = value.values[key];
      
      // Handle array of files (multiple uploads)
      if (Array.isArray(val)) {
        val.forEach((item, index) => {
          // Only upload NEW files (those with lastModified)
          if (item && item.name && item.lastModified) {
            mediaUploads.push({ key, value: item, index });
          }
        });
      }
      // Handle single file
      else if (val && val.name && val.lastModified) {
        mediaUploads.push({ key, value: val, index: null });
      }
    });
    
    if (!mediaUploads || mediaUploads.length === 0) return of(null);
    
    return mediaUploads.map(m =>
      this.mediaService
        .attach(m.value)
        .pipe(map(media => ({ ...media, key: m.key, index: m.index })))
    );
  }

  // onFormSelectedChange(formId: number) {
  //   this.formId = formId;
  // }

  onSubmitClick() {
    if (this.isFormInvalid()) {
      return;
    } 
    
    forkJoin(this.handleMedias(this.formAttributesEx))
      .pipe(
        switchMap(results => {
          const value = this.getFormValue(this.formAttributesEx);
          
          const medias = results as unknown as MediaEx[];
          
          // Process media files
          Object.keys(value.values).forEach(key => {
            const val = value.values[key];
            
            // Handle array of files (multiple uploads)
            if (Array.isArray(val)) {
              const mediaIds = [];
              
              val.forEach((item, index) => {
                if (item && item.isExisting && item.id) {
                  // Existing file - keep its ID
                  mediaIds.push(item.id);
                } else if (item && item.lastModified) {
                  // New file - find its uploaded ID from medias array
                  const uploadedMedia = medias.find(m => m.key === key && m.index === index);
                  if (uploadedMedia) {
                    mediaIds.push(uploadedMedia.id);
                  }
                }
              });
              
              // Update value with combined IDs
              if (mediaIds.length > 0) {
                value.values[key] = mediaIds;
              }
            }
            // Handle single file
            else if (val && typeof val === 'object') {
              if (val.isExisting && val.id) {
                // Existing file - keep its ID
                value.values[key] = [val.id];
              } else if (val.lastModified) {
                // New file - find its uploaded ID
                const uploadedMedia = medias.find(m => m.key === key && m.index === null);
                if (uploadedMedia) {
                  value.values[key] = [uploadedMedia.id];
                }
              }
            }
          });
          
          Object.keys(value.values).map((ele) => {
              if (value.values[ele][0] === null) {
                value.values[ele] = [];
              } else if (isEmpty(value.values[ele]) || value.values[ele][0] == -1) {
                delete value.values[ele];
              }
          });
          // Edit safety: restore required values wiped by tag-selector races.
          if (this.isEditing && this.post?.values) {
            (this.formAttributesEx?.results || []).forEach(fa => {
              if (!fa?.required || !fa.key) {
                return;
              }
              const current = value.values[fa.key];
              const missing =
                current == null ||
                isEmpty(current) ||
                current[0] == null ||
                current[0] === -1;
              const original = this.post.values[fa.key];
              if (
                missing &&
                Array.isArray(original) &&
                original.length > 0 &&
                original[0] != null &&
                original[0] !== -1 &&
                !this.hasChangedParentDriver(fa, value.values)
              ) {
                value.values[fa.key] = original;
              }
            });
          }
          if (this.isEditing) {
            this.post.title = value.title;
            this.post.content = value.content;
            this.post.proceso = value.proceso; // mq
            this.post.institucion = value.institucion; // mq
            this.post.values = value.values;
            this.post.parent_id = (this.post.parent) ? this.post.parent.id : null;
            return this.postService.update(this.post);
          }

          let isLocationTypeGeenral = false;
          Object.entries(value.values).forEach(element => {
            if (element[1][0] === 'General') {
              isLocationTypeGeenral = true;
            }
          });
          let mgmt_lev_1: any;
          let mgmt_lev_2: any;
          let mgmt_lev_3: any;
          if (isLocationTypeGeenral) {
            mgmt_lev_1 = globals.selectedRegion;
            mgmt_lev_2 = globals.selectedZone;
            mgmt_lev_3 = globals.selectedWoreda;
          } else {
            mgmt_lev_1 = this.post.mgmt_lev_1;
            mgmt_lev_2 = this.post.mgmt_lev_2;
            mgmt_lev_3 = this.post.mgmt_lev_3;
          }
          return this.postService.create({
            ...value,
            mgmt_lev_1,
            mgmt_lev_2,
            mgmt_lev_3
          });
        }),
        catchError(() => of(null))
      )
      .subscribe((post: Post) => {
        if (post) {
          post['isEditing'] = this.isEditing;
        }
        this.store.dispatch(
          post ? new SubmitPostSuccess(post) : new SubmitPostFailure()
        );
      });
  }

  getformAttributesExUpdate(newFormData) {
    this.formAttributesEx.results.forEach((form, index) => {
      if (form.label === RiskFilters.DISTRICT) {
        const zone = [];
        const formAttributeData = newFormData.filter(el => el.queryParam === PostFilters.District);
        formAttributeData[0].values.forEach(zoneValue => {
          zone.push(zoneValue.name);
        });
        this.formAttributesEx.results[index]['options'] = zone
      }
      if (form.label === RiskFilters.WARD) {
        this.formAttributesEx.results[index]['options'] = [];
      }
    });
  }
  getformAttributesExUpdateZone(newFormData) {
    // this.formAttributesEx.results.forEach((form, index) => {
    //   if(form.label === RiskFilters.WARD) {
    //     const woreda = [];
    //     const formAttributeData = newFormData.filter(el => el.queryParam === PostFilters.Ward);
    //     formAttributeData[0].values.forEach(zoneValue => {
    //       woreda.push(zoneValue.name);
    //     });
    //     this.formAttributesEx.results[index]['options'] = woreda
    //   }
    // });
  }
}

export interface FormAttributeEx extends FormAttribute {
  value: any;
  /** Client-side validation flag (e.g. datetime out of allowed range). */
  clientInvalid?: boolean;
}

export interface FormAttributesEx extends BaseModelArray<FormAttributeEx> { }

export interface MediaEx extends Media {
  key: string;
  index?: number;
}
